import React, { useState, useEffect, useMemo, useContext } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Card, CardBody, Container, Row, Col } from "reactstrap";

import CommonTabHeader from "Components/Common/CommonTabHeader";
import { LIBRARY_PATHS } from "Routes/paths";
import DOMPurify from "dompurify";
import { useTemplateApi } from "modules/Library/Template/api/useTemplateService";
import JsBarcode from "jsbarcode";
import Template from "../helper/template.types";
import { BreadcrumbContext } from "Layouts/Header";

const ViewTemplate: React.FC = () => {
  document.title = "View Template | ZENTIXS VISTA";

  const location = useLocation();
  const { id } = useParams();
  const templateId = Number(id);

  const [templateName, setTemplateName] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const breadcrumbContext = useContext(BreadcrumbContext);

  // API Hook
  const { DetailSelectQuery } = useTemplateApi({
    templateid: templateId,
    selectAllEnabled: false,
  });

  // ✅ Load data properly
  useEffect(() => {
    // Try state first
    if (location.state) {
      const stateData = location.state as Template;
      setTemplateName(stateData.name || stateData.templatename || "");
      setHtmlContent(stateData.json || stateData.htmlcontent || stateData.designtemplate || "");
      return;
    }

    // Otherwise use API data
    if (DetailSelectQuery.data?.data) {
      const apiData =
        DetailSelectQuery.data.data[0] || DetailSelectQuery.data.data;
      setTemplateName(apiData.name || apiData.templatename || "");
      setHtmlContent(apiData.json || apiData.htmlcontent || apiData.designtemplate || "");
    }
  }, [location.state, DetailSelectQuery.data]);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Template", link: LIBRARY_PATHS.TEMPLATE_LIST },
      { text: templateName || "View Template" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  // ✅ Convert Barcode natively to static Base64 image before DOM injection
  const generateBarcodeImage = (value: string) => {
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, value, {
        format: "CODE128",
        width: 2,
        height: 40,
        displayValue: false,
        margin: 0,
      });
      return canvas.toDataURL("image/png");
    } catch {
      return "";
    }
  };

  // ✅ Generic placeholder replacer
  const replacePlaceholders = (template: string, data: Record<string, any>) => {
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      return data[key.trim()] ?? "";
    });
  };

  const dosingDates = [
  { period: 1, date: "01-Jul-2026" },
  { period: 2, date: "09-Jul-2026" },
  // { period: 3, date: "17-Jul-2026" },
  // { period: 4, date: "25-Jul-2026" },
];

const periodRows = dosingDates
  .map(
    (item) => `
      <div class="id_period_cell">
          <span class="id_label">Period ${item.period}</span>
          <span class="id_value">${item.date}</span>
      </div>
    `
  )
  .join("");

  // ✅ Dynamic data (mock)
  const data = {
    FullName: "John Doe",
    StudyCode: "BE/21/CT-05",
    SubjectNo: "02",
    PeriodRows: periodRows,
    CompanyLogo:"https://adrtatech.com/assets/img/logo-color.png",
    Period1DosingDate: "01-Jul-2026",
    Period2DosingDate:"09-Jul-2026",
    Period3DosingDate:"17-Jul-2026",
    Period4DosingDate:"25-Jul-2026",
    VolunteerCode: "000276",
    Language:"English",
    Initial: "JD",
    Dob : "30-Dec-2001",
    PrintedOn: "26-Jul-2026",
    AgeSex: "28 / Male",
    BloodGroup: "B+",
    VolunteerSign:"https://www.vhv.rs/dpng/d/586-5864567_fake-signature-png-transparent-png.png",
    EnrollmentNo: "STUDY-567-001",
    Allergy: "No Known Allergy",
    Contact: "+91 9876543210",
    SubjectId: "SUBJ-1024",
    ValidDate: "30-Dec-2023",
    Barcode: `<img src="${generateBarcodeImage("123456789")}" alt="Barcode"/>`,
    PhotoUrl:
      "https://as2.ftcdn.net/v2/jpg/05/11/55/91/1000_F_511559113_UTxNAE1EP40z1qZ8hIzGNrB0LwqwjruK.jpg",
  };

  // ✅ Compute final HTML safely
  const finalHtml = useMemo(() => {
    if (!htmlContent) return "";
    const replaced = replacePlaceholders(htmlContent, data);
    return DOMPurify.sanitize(replaced, {
      ADD_TAGS: ["style"],
      WHOLE_DOCUMENT: true,
      FORCE_BODY: true,
    });
  }, [htmlContent]);

  const isLoading = DetailSelectQuery.isLoading && !templateName;

  return (
    <Container
      fluid
      style={{
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "stretch",
      }}
    >
      <CommonTabHeader
        title="View Template"
        activeTab="View Template"
        exitPath={LIBRARY_PATHS.TEMPLATE_LIST}
      />

      {/* <Container fluid className="px-0 mt-3"> */}
      <Row className="pt-2 flex-grow-1 overflow-hidden">
        <Col lg={12} className="h-100 pb-2">
          <Card className="h-100 shadow-none border-0 mb-0">
            <CardBody className="p-3 d-flex flex-column h-100 overflow-hidden">
              {isLoading ? (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* HEADER */}
                  <div className="mb-4">
                    <h5 className="text-primary">{templateName}</h5>
                  </div>

                  {/* TEMPLATE RENDER */}
                  <div
                    className="template-preview-content p-4"
                    style={{ minHeight: "500px" }}
                    dangerouslySetInnerHTML={{ __html: finalHtml }}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* </Container> */}
    </Container>
    // </div>
  );
};

export default ViewTemplate;
